import { useCallback, useEffect, useMemo, useState } from "react";

import {
  TEMPORAL_COMPARE_DEFAULT_LIMITS,
  fetchTemporalCompare,
  normalizeTemporalCompareRequest,
  validateTemporalCompareRequest,
  type TemporalCompareRequest,
  type TemporalCompareResult,
} from "../../api/temporalCompare";

interface TemporalCompareLoadState {
  key: string | null;
  data: TemporalCompareResult | null;
  error: Error | null;
  loading: boolean;
}

export interface TemporalCompareState {
  data: TemporalCompareResult | null;
  error: Error | null;
  isLoading: boolean;
  refresh: () => void;
}

/**
 * Stable identity for one exact comparison. Server defaults are materialized so
 * an omitted default and the equivalent explicit value share one identity.
 */
export function temporalCompareRequestKey(request: TemporalCompareRequest): string {
  validateTemporalCompareRequest(request);
  const normalized = normalizeTemporalCompareRequest(request);
  return JSON.stringify([
    normalized.project_id,
    normalized.root_refs.map((root) => [root.entity_kind, root.id]),
    normalized.before_as_of,
    normalized.after_as_of,
    normalized.recorded_as_of ?? null,
    normalized.snapshot_token ?? null,
    normalized.direction ?? "both",
    normalized.max_depth ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_depth,
    normalized.max_records ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_records,
    normalized.max_paths ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_paths,
    normalized.max_path_steps ?? TEMPORAL_COMPARE_DEFAULT_LIMITS.max_path_steps,
  ]);
}

function aborted(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : error instanceof Error && error.name === "AbortError";
}

/** Load one exact, bounded bitemporal comparison without retaining stale data. */
export function useTemporalCompare(request: TemporalCompareRequest | null): TemporalCompareState {
  const rootsKey = request?.root_refs.map((root) => `${root.entity_kind}:${root.id}`).join("|") ?? null;
  const key = useMemo(() => request ? temporalCompareRequestKey(request) : null, [
    request?.project_id,
    rootsKey,
    request?.before_as_of,
    request?.after_as_of,
    request?.recorded_as_of,
    request?.snapshot_token,
    request?.direction,
    request?.max_depth,
    request?.max_records,
    request?.max_paths,
    request?.max_path_steps,
  ]);
  const [reload, setReload] = useState(0);
  const [state, setState] = useState<TemporalCompareLoadState>({
    key: null,
    data: null,
    error: null,
    loading: false,
  });

  useEffect(() => {
    if (!request || !key) {
      setState({ key: null, data: null, error: null, loading: false });
      return;
    }

    const controller = new AbortController();
    let active = true;
    setState({ key, data: null, error: null, loading: true });
    void fetchTemporalCompare(request, { signal: controller.signal }).then(
      (envelope) => {
        if (active) setState({ key, data: envelope.data, error: null, loading: false });
      },
      (error: unknown) => {
        if (!active || aborted(error)) return;
        setState({
          key,
          data: null,
          error: error instanceof Error ? error : new Error("Could not load the temporal comparison."),
          loading: false,
        });
      },
    );

    return () => {
      active = false;
      controller.abort();
    };
  }, [key, reload]);

  const refresh = useCallback(() => {
    if (!key) return;
    setState({ key, data: null, error: null, loading: true });
    setReload((value) => value + 1);
  }, [key]);

  if (state.key !== key) {
    return { data: null, error: null, isLoading: key !== null, refresh };
  }
  return { data: state.data, error: state.error, isLoading: state.loading, refresh };
}
